import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  PersonAdd as ReferralIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import { getUserReferralHistory, ReferralCodeUsage } from '../services/referralCode';
import { useAuth } from '../contexts/AuthContext';

const ReferralCodeHistory: React.FC = () => {
  const { getIdToken } = useAuth();
  const [history, setHistory] = useState<ReferralCodeUsage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getUserReferralHistory(getIdToken);
      
      // Ensure data is an array
      if (Array.isArray(data)) {
        setHistory(data);
      } else {
        console.error('Expected array but got:', typeof data, data);
        setHistory([]);
        setError('Invalid response format from server');
      }
    } catch (err: any) {
      console.error('Error loading referral history:', err);
      setHistory([]);
      setError(err.message || 'Failed to load referral history');
    } finally {
      setLoading(false);
    }
  }, [getIdToken]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);



  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button size="small" onClick={loadHistory} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (history.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <ReferralIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Referral Codes Used
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You haven't used any referral codes yet. Apply a code to see it here!
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                 <Typography variant="h6" component="h3">
           Referral Codes Used
         </Typography>
        <Button
          size="small"
          onClick={() => setExpanded(!expanded)}
          endIcon={expanded ? <CollapseIcon /> : <ExpandIcon />}
        >
          {expanded ? 'Collapse' : 'Expand'}
        </Button>
      </Box>

      <List dense>
        {Array.isArray(history) && history.slice(0, expanded ? undefined : 3).map((usage, index) => (
          <React.Fragment key={`${usage.code}-${usage.usedAt}`}>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <ReferralIcon color="primary" />
              </ListItemIcon>
                             <ListItemText
                 primary={
                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                     <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                       {usage.code}
                     </Typography>
                   </Box>
                 }
               />
            </ListItem>
            {Array.isArray(history) && index < history.length - 1 && <Divider variant="inset" component="li" />}
          </React.Fragment>
        ))}
      </List>

      {Array.isArray(history) && history.length > 3 && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {expanded ? 'Showing all' : `Showing ${Math.min(3, history.length)} of ${history.length}`}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ReferralCodeHistory;
